// Pricing table, price generation, propagation, and credits API calls.

import type { CalendarKindCapacity, CalendarTableResponse } from "../types/api";
import type { PriceStepReason } from "../types/priceJustification";
import { ActiveConnectionChangedError, apiFetch, PmsNotConfiguredError } from "./apiClient";
// The send names the property the Prices page was loaded for, and the service
// refuses it once another one is open: it pushes the page's entries to the PMS
// of whichever property is open. A recalculation names the property the
// strategy page's strategies were read for, as their save does.
import {
    activeConnectionChangedError,
    isActiveConnectionChangedRefusal,
    loadedConnectionQuery,
    refusedWriteError,
    withPageConnection,
    type LoadedConnectionId,
} from "./loadedConnection";
import { waitForJobToSettle } from "./jobPolling";
import type { UploadedDerivedPlan } from "./uploadLogsApi";

// -- Special dates ----------------------------------------------------------

export interface SpecialDateInfo {
    name: string;
    type: "premium" | "discount";
    /** The strategy's premium or discount. Absent for a read-only viewer, who is not sent it. */
    multiplier?: number;
}

export async function fetchSpecialDates(
    from: string,
    to: string,
): Promise<Record<string, SpecialDateInfo>> {
    const params = new URLSearchParams({ from, to });
    const res = await apiFetch(`/api/pricing-table/special-dates?${params}`);
    if (!res.ok) return {};
    const data = await res.json();
    return data.specialDates ?? {};
}

// -- Pricing Table ----------------------------------------------------------

export interface PropagateEntry {
    date: string;
    roomKindId: string;
    roomKindName?: string;
    minLos?: number;
    occupancyPrices: { occupancy: number | string; price: number }[];
}

/**
 * Which layers of an upload reach the PMS. Prices and minimum stay are
 * independent: either can be sent without the other. Mirrors
 * `RateUpdateInclude` on the backend.
 */
export interface UploadInclude {
    prices: boolean;
    minLos: boolean;
}

/**
 * Prices only. Minimum stay is opt-in because every draft entry carries a
 * `minLos` (defaulting to 1) and sending it unasked overwrites whatever
 * minimum the hotel configured in its PMS.
 */
export const DEFAULT_UPLOAD_INCLUDE: UploadInclude = { prices: true, minLos: false };

export interface PropagateResult {
    success: boolean;
    updatedCount: number;
    errors: string[];
}

// -- Propagation job (batched upload) ----------------------------------------

export interface PropagateJobStart {
    jobId: string;
    status: "running";
    totalEntries: number;
    completedEntries: number;
    dryRun?: boolean;
}

export interface PropagateJobStatus {
    jobId: string;
    status: "running" | "completed" | "failed";
    /** Entries actually in flight to the PMS — the progress-bar denominator. */
    totalEntries: number;
    completedEntries: number;
    /**
     * Entries the user asked to send, before server-side suppression. Optional
     * because the provider builds optimistic job objects locally before the
     * first status poll comes back.
     */
    requestedEntries?: number;
    /** How many requested cells the server skipped because they are sold out. */
    skippedSoldOut?: number;
    errors: string[];
    warnings?: string[];
    startedAt: string;
    completedAt?: string;
    dryRun?: boolean;
    /**
     * Which half of the upload is running. `settling` is the deliberate pause
     * that lets the PMS finish re-deriving before the derived rate plans are
     * written — without it the progress bar just freezes with no explanation.
     */
    phase?: "base" | "settling" | "derived";
    /**
     * How many of `totalEntries` are base-plan prices. The rest are derived
     * rate plans, which the operator did not choose cell by cell — so "N prices
     * uploaded" counts this, not the inflated total.
     */
    baseEntries?: number;
    derivedPlans?: UploadedDerivedPlan[];
    /**
     * The price cells the job carries — one night of one room kind each, the
     * unit the operator chose. Optional for the same reason as
     * `requestedEntries`, and on a server that predates it.
     */
    prices?: number;
    /** Cells whose every write reached the PMS. Settled once the job has finished. */
    deliveredPrices?: number;
    /** Cells the PMS refused — sending them again changes nothing until the cause is fixed there. */
    refusedPrices?: number;
    /**
     * Cells Optivo itself held back, with nothing of theirs refused: the PMS
     * was asked to write nothing, and a later send can write them. A job
     * short only of these is `failed`, since not every cell landed, and is no
     * failure. Absent from a server that predates the count, where such a
     * cell was counted as refused.
     */
    heldPrices?: number;
    /** Cells that got no answer — what a retry sends again. */
    undeliveredPrices?: number;
    /**
     * Set when nothing was sent because the PMS keeps its price list in another
     * currency. `errors` carries the same fact as an English sentence.
     */
    currencyMismatch?: CurrencyMismatchSummary;
    /**
     * Whether the job puts a saved price list back: a restore, from an
     * upload's restore point or from a file, or the retry of one. Such a job
     * commits no drafts, so what becomes of a night of it that did not land is
     * not what becomes of a send's, and the banner says it in a restore's own
     * words. Absent on the job the provider builds before the first status
     * poll, and from a server that predates it: a job that does not say is
     * read as a send, as every job was.
     */
    restoresPriceList?: boolean;
}

/** One way a send's prices and the PMS price list disagree on currency. */
export interface CurrencyMismatch {
    /** The currency Optivo's prices are in. */
    pricedIn: string;
    /** The currency the PMS keeps the price list in. */
    priceListCurrency: string;
    /** The room types affected, by name. */
    roomTypes: string[];
    /** Whether every room type in the send is affected. */
    allRoomTypes: boolean;
}

export interface CurrencyMismatchSummary {
    providerType: string;
    mismatches: CurrencyMismatch[];
}

/** Whether a send would be stopped for its currency, asked before sending. */
export interface PriceListCurrencyStatus {
    providerType: string;
    /** The currency Optivo prices this property in. */
    pricedIn: string;
    /** Whether the PMS was asked at all. */
    checked: boolean;
    /** Whether a send of these room types would be stopped. */
    blocked: boolean;
    mismatches: CurrencyMismatch[];
    /** Room types whose price-list currency could not be read. */
    uncheckedRoomTypes: number;
}

export async function fetchPriceListCurrencyStatus(
    roomKindIds: string[],
): Promise<PriceListCurrencyStatus> {
    const query = roomKindIds.length > 0
        ? `?roomKindIds=${encodeURIComponent(roomKindIds.join(","))}`
        : "";
    const res = await apiFetch(withPageConnection(`/api/pricing-table/propagate/currency-check${query}`));
    if (res.status === 409) throw await refusedWriteError(res, "fetchPriceListCurrencyStatus failed");
    if (!res.ok) throw new Error(`fetchPriceListCurrencyStatus failed: ${res.status}`);
    return res.json();
}

/** `"night"`: one minimum stay per night for every room type. `"roomType"`: each room type its own. */
export type MinStayScope = "night" | "roomType";

/** How the PMS keeps minimum stays, asked before minimum stays are sent. */
export interface MinStayScopeStatus {
    providerType: string;
    /** Null where the PMS could not tell. */
    scope: MinStayScope | null;
    /** Whether the PMS was asked: only one that leaves it to the hotel (Previo) is. */
    read: boolean;
}

/**
 * A scope answer as a page reads it, whichever of the two routes sent it. A
 * server from before 21 Oct 2026 may still send `changes` beside it, the
 * count of what the send would change in the PMS that the dialogs no longer
 * draw; it is left where it is.
 */
export function readMinStayScopeStatus(data: {
    providerType: string;
    scope?: MinStayScope | null;
    read?: unknown;
}): MinStayScopeStatus {
    return {
        providerType: data.providerType,
        scope: data.scope ?? null,
        read: data.read === true,
    };
}

/**
 * Whether the open property's PMS keeps one minimum stay per night for every
 * room type — a Previo hotel kept per season — in which case a send levels each
 * night to the strictest of its room types. The send reads again for itself.
 */
export async function fetchMinStayScope(): Promise<MinStayScopeStatus> {
    const res = await apiFetch(withPageConnection("/api/pricing-table/propagate/min-stay-scope"));
    if (res.status === 409) throw await refusedWriteError(res, "fetchMinStayScope failed");
    if (!res.ok) throw new Error(`fetchMinStayScope failed: ${res.status}`);
    return readMinStayScopeStatus(await res.json());
}

/**
 * The sold-out (room kind, night) cells from `from` to `to`, which a send drops
 * whatever it is offered, read from the snapshot the send reads.
 */
export async function fetchSoldOutCells(
    from: string,
    to: string,
): Promise<Array<{ roomKindId: string; date: string }>> {
    const query = `?from=${encodeURIComponent(from)}&to=${encodeURIComponent(to)}`;
    const res = await apiFetch(withPageConnection(`/api/pricing-table/propagate/sold-out${query}`));
    if (res.status === 409) throw await refusedWriteError(res, "fetchSoldOutCells failed");
    if (!res.ok) throw new Error(`fetchSoldOutCells failed: ${res.status}`);
    const data = await res.json();
    return Array.isArray(data.cells) ? data.cells : [];
}

export interface RatePlanOption {
    ratePlanId: string | number;
    name: string;
    isBasePlan: boolean;
    isNonRefundable: boolean;
}

export interface RatePlansResponse {
    plans: RatePlanOption[];
    basePlan: RatePlanOption | null;
}

export async function fetchRatePlans(): Promise<RatePlansResponse> {
    // Provider-agnostic alias for the legacy /api/eqc/rate-plans endpoint.
    const res = await apiFetch("/api/pms/rates/rate-plans");
    if (!res.ok) throw new Error(`fetchRatePlans failed: ${res.status}`);
    return res.json();
}

export interface RoomTypeOption {
    roomKindId: string | number;
    roomKindName: string;
    capacity: number;
    extraCapacity: number;
    /**
     * The rooms of this kind the pricing calendar draws, one per unit — what the
     * room kind's own occupancy is counted over. Absent where the PMS lists no
     * rooms one by one.
     */
    roomCount?: number;
}

export async function fetchRoomTypes(): Promise<RoomTypeOption[]> {
    // Provider-agnostic alias for the legacy /api/eqc/room-types endpoint.
    const res = await apiFetch("/api/pms/rates/room-types");
    if (!res.ok) throw new Error(`fetchRoomTypes failed: ${res.status}`);
    const data = await res.json();
    return data.roomTypes;
}

export async function propagatePricesToPms(
    entries: PropagateEntry[],
    prlId?: string,
    draftIds?: number[],
    dryRun?: boolean,
    include?: UploadInclude,
): Promise<PropagateJobStart> {
    const res = await apiFetch(withPageConnection("/api/pricing-table/propagate"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ entries, prlId, draftIds, dryRun, include }),
    });
    if (!res.ok) {
        // The server rejects an empty scope or an unsupported restriction push
        // with a message worth showing, rather than a bare status code.
        const detail = await res.json().catch(() => null);
        if (res.status === 409) {
            // Three conflicts share the status: the page was loaded for another
            // property than the one open now, no configured PMS, and a
            // scheduled sync holding the push lock. Only the second is about
            // configuration, and only the last passes if tried again.
            if (isActiveConnectionChangedRefusal(detail)) {
                throw activeConnectionChangedError(detail.error || "The page was loaded for another property");
            }
            if (detail?.error === "PMS_NOT_CONFIGURED") {
                throw new PmsNotConfiguredError(detail.message || "Property Management not configured");
            }
            throw new PushBusyError(detail?.error || "A price sync is already running");
        }
        throw new Error(detail?.error || `propagatePricesToPms failed: ${res.status}`);
    }
    return res.json();
}

/** A scheduled sync is pushing to the same PMS; the send can be tried again shortly. */
export class PushBusyError extends Error {
    constructor(message: string) {
        super(message);
        this.name = "PushBusyError";
    }
}

export async function fetchPropagationStatus(): Promise<PropagateJobStatus | null> {
    const res = await apiFetch("/api/pricing-table/propagate/status");
    if (!res.ok) throw new Error(`fetchPropagationStatus failed: ${res.status}`);
    const data = await res.json();
    return data.job ?? null;
}

export async function resumePropagation(): Promise<PropagateJobStart> {
    const res = await apiFetch(withPageConnection("/api/pricing-table/propagate/resume"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
    });
    if (!res.ok) throw await refusedWriteError(res, "resumePropagation failed");
    return res.json();
}

/**
 * Clear the open property's finished upload. Only a refusal for another
 * property throws — that one the shell has to hear about; any other failure
 * leaves the banner to the next read, as it always has.
 */
export async function clearPropagationStatus(): Promise<void> {
    const res = await apiFetch(withPageConnection("/api/pricing-table/propagate/status/clear"), {
        method: "POST",
        headers: { "Content-Type": "application/json" },
    });
    if (res.status !== 409) return;
    const error = await refusedWriteError(res, "clearPropagationStatus failed");
    if (error instanceof ActiveConnectionChangedError) throw error;
}

export async function fetchPricingTable(
    from: string,
    to: string,
): Promise<CalendarTableResponse> {
    const params = new URLSearchParams({ from, to });
    const res = await apiFetch(`/api/pricing-table?${params}`);
    if (!res.ok) {
        if (res.status === 409) {
            const data = await res.json();
            throw new PmsNotConfiguredError(data.message || "Property Management not configured");
        }
        throw new Error(`fetchPricingTable failed: ${res.status}`);
    }
    return res.json();
}

/**
 * Per-night unit accounting per room kind, without the rest of the table.
 *
 * The Price Drafts calendar draws its grid from the draft entries, so it needs
 * only this block to report availability. Failures are the caller's to swallow:
 * a missing unit count is a missing badge, never a blank calendar.
 */
export async function fetchCalendarCapacity(
    from: string,
    to: string,
): Promise<CalendarKindCapacity[]> {
    const params = new URLSearchParams({ from, to });
    const res = await apiFetch(`/api/pricing-table/capacity?${params}`);
    if (!res.ok) {
        if (res.status === 409) {
            const data = await res.json();
            throw new PmsNotConfiguredError(data.message || "Property Management not configured");
        }
        throw new Error(`fetchCalendarCapacity failed: ${res.status}`);
    }
    const data = await res.json();
    return data.capacity ?? [];
}

// -- Full Price Generation --------------------------------------------------

export interface GenerationDraftResult {
    month: string;
    rangeFrom: string;
    rangeTo: string;
    entriesCount: number;
    draftId: number;
}

export type GenerationJobStatus = "running" | "completed" | "error";

export interface GenerationJob {
    jobId: string;
    userId: string;
    status: GenerationJobStatus;
    currentChunk: number;
    totalChunks: number;
    currentChunkLabel: string;
    draftsCompleted: GenerationDraftResult[];
    totalEntries: number;
    creditsUsed: number;
    cleanedUp: number;
    errors: string[];
    errorMessage?: string;
    startedAt: string;
    completedAt?: string;
}

/**
 * Recalculates the whole horizon, a year and a half. `loadedConnectionId` names the
 * property the page's strategies were read for, as their save does: the service
 * refuses the run with `ActiveConnectionChangedError` once another property is
 * open, rather than reprice that one. Undefined names nothing.
 */
export async function triggerFullRegeneration(
    loadedConnectionId?: LoadedConnectionId,
): Promise<{ jobId: string; status: GenerationJobStatus }> {
    const res = await apiFetch(`/api/autopilot/generate${loadedConnectionQuery(loadedConnectionId)}`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({}),
    });
    if (!res.ok) {
        if (res.status === 409) {
            // Both refusals answer 409: a page loaded for another property,
            // and a property with no PMS configured.
            const data = await res.json().catch(() => ({}));
            if (isActiveConnectionChangedRefusal(data)) {
                throw activeConnectionChangedError(data.error || `triggerFullRegeneration failed: ${res.status}`);
            }
            throw new PmsNotConfiguredError(data.message || "PMS not configured");
        }
        throw new Error(`triggerFullRegeneration failed: ${res.status}`);
    }
    return res.json();
}

export async function fetchGenerationJobStatus(): Promise<GenerationJob | null> {
    const res = await apiFetch("/api/autopilot/generate/status");
    if (!res.ok) throw new Error(`fetchGenerationJobStatus failed: ${res.status}`);
    const data = await res.json();
    return data.job ?? null;
}

/** Thrown when generation outlives the client deadline, so callers can say so specifically. */
export class GenerationTimeoutError extends Error {
    constructor(public readonly waitedMs: number) {
        super(`Generation timed out after ${Math.round(waitedMs / 1000)}s`);
        this.name = "GenerationTimeoutError";
    }
}

export interface WaitForGenerationOptions {
    /**
     * Called once when the job passes {@link slowAfterMs} without finishing, so
     * the UI can say "still working" instead of showing a spinner that is
     * indistinguishable from a hang.
     */
    onSlow?: () => void;
    /** How long before the job counts as slow. Default 20s. */
    slowAfterMs?: number;
    /** Called on each successful poll, so a long run can show real progress. */
    onProgress?: (job: GenerationJob) => void;
    /** Hard client deadline. Default 5 minutes. */
    maxWaitMs?: number;
    /**
     * Where to read the job from. Defaults to the active connection's own
     * status endpoint; the admin console passes its per-connection one, since
     * the run it started belongs to a property the admin is not connected to.
     * The retry, deadline and slow-warning behaviour below is the same problem
     * either way, so it is not written twice.
     */
    fetchStatus?: () => Promise<GenerationJob | null>;
    /** Stops the wait; the promise then rejects with an `AbortError`. */
    signal?: AbortSignal;
}

/**
 * Poll the generation job until it completes or errors.
 *
 * The retrying, the deadline and the slow warning are `waitForJobToSettle`'s —
 * the same problem the property analysis and the admin recalculation have, so
 * it is solved once. What is generation's own is what counts as finished, and
 * what to throw when nothing ever does.
 */
export async function waitForGenerationComplete(
    options: WaitForGenerationOptions = {},
): Promise<GenerationJob> {
    return waitForJobToSettle<GenerationJob>({
        fetchStatus: options.fetchStatus ?? fetchGenerationJobStatus,
        isSettled: (job) => job.status === "completed" || job.status === "error",
        onProgress: options.onProgress,
        onSlow: options.onSlow,
        slowAfterMs: options.slowAfterMs,
        maxWaitMs: options.maxWaitMs,
        onTimeout: (waitedMs) => new GenerationTimeoutError(waitedMs),
        onVanished: () => new Error("Generation job disappeared"),
        signal: options.signal,
    });
}

/** Trigger a single poll cycle: snapshot bookings, detect changes, recalculate affected prices and update drafts. */
export async function triggerPollRefresh(): Promise<{ recalculated: number; newBookingCount: number }> {
    const res = await apiFetch("/api/autopilot/refresh", { method: "POST" });
    if (!res.ok) throw new Error(`triggerPollRefresh failed: ${res.status}`);
    return res.json();
}

// -- Credits ----------------------------------------------------------------

export interface CreditsResponse {
    balance: number;
}

export async function fetchCredits(): Promise<CreditsResponse> {
    const res = await apiFetch("/api/credits");
    if (!res.ok) throw new Error(`fetchCredits failed: ${res.status}`);
    const data = await res.json();
    if (!data.success) throw new Error(data.error || "Failed to fetch credits");
    return data.credits;
}

// -- Price Simulation -------------------------------------------------------

export interface SimulationStep {
    /** English label — the fallback for steps the UI translation table doesn't know. */
    label: string;
    /**
     * Language-neutral key for `label` (mirrors the backend `labelKey`). The
     * simulator resolves it against `t.priceSimulator` so the breakdown reads
     * in the user's language.
     */
    labelKey?: string;
    /** Interpolation values for `labelKey`. */
    labelParams?: Record<string, string | number>;
    factor: number;
    effect: number;
    priceAfter: number;
    active: boolean;
    /**
     * Decomposition rows (e.g. "Room-kind occupancy" / "Property-wide demand")
     * that surface contributions already baked into the additive base. They
     * do NOT shift the running price; the simulator displays them grouped
     * under the base row to explain where the discount/premium came from.
     */
    informational?: boolean;
    /**
     * When the step is inactive, why it did not apply (mirrors the backend
     * `PriceStepReason`). The simulator uses it to explain skipped steps —
     * e.g. a weekend premium replaced by a special-day premium.
     */
    reason?: PriceStepReason;
}

export interface ScenarioResult {
    scenario: "common" | "lowDemand" | "highDemand";
    label: string;
    basePrice: number;
    /** Raw historical / room-kind base price BEFORE the occupancy blend was applied. */
    unblendedBasePrice?: number;
    basePriceExplanation?: string;
    /** Language-neutral key for `basePriceExplanation` (mirrors the backend). */
    basePriceExplanationKey?: string;
    /** Interpolation values for `basePriceExplanationKey`. */
    basePriceExplanationParams?: Record<string, string | number>;
    /**
     * Present when the base price was set from the market — mirrors the
     * backend `MarketFollowBase`. The simulator stands a median in; live
     * pricing carries the captured one.
     */
    marketFollow?: {
        basePrice: number;
        marketMedian: number;
        positionPct: number;
        entryBasePrice: number;
        ladder: number;
        pricedCount: number;
    };
    occupancyRate?: number;
    steps: SimulationStep[];
    losRestriction?: {
        minLos: number;
        applied: boolean;
        originalMinLos?: number;
        reason?: "lowOccupancy" | "lowDemandIndex" | "demandOutlook";
    };
    finalPrice: number;
    guardrails: {
        minPriceFloor: number;
        /**
         * Which bound `minPriceFloor` is: the minimum price, the base-price
         * hold (a date beyond the booking window, held at its base price as
         * adjusted for the night) or the last-minute minimum. Absent from a
         * server that predates it, which reads as the minimum.
         */
        minPriceFloorSource?: "minPrice" | "baseHold" | "lastMinute";
        /**
         * The signed whole percent the simulated month's Seasonality moved the
         * minimum or the last-minute minimum by, where that minimum follows
         * Seasonality and the month moved it. Absent otherwise, and from a
         * server that predates it.
         */
        minPriceFloorSeasonPct?: number;
        maxPriceCeiling: number;
        clampedPrice: number;
        isClamped: boolean;
    };
}

export interface SimulationResult {
    currency: string;
    scenarios: ScenarioResult[];
    previewRoomKindId?: string;
}

export async function simulatePrice(
    params: Record<string, unknown>,
): Promise<SimulationResult> {
    const { daysUntilArrival, roomKindId, occupancyOverride, propertyOccupancyOverride, dayOfWeekOverride, bookingSpeedOverride, hoursUntilArrival, forceHoliday, forceBridgeDay, forceShoulderDay, forceGapFilling, simulationMonth, demandOverride, weatherOverride, ...pricingParams } = params;
    const res = await apiFetch("/api/autopilot/simulate-price", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
            params: pricingParams,
            ...(daysUntilArrival != null ? { daysUntilArrival } : {}),
            ...(roomKindId != null ? { roomKindId } : {}),
            ...(occupancyOverride != null ? { occupancyOverride } : {}),
            ...(propertyOccupancyOverride != null ? { propertyOccupancyOverride } : {}),
            ...(dayOfWeekOverride != null ? { dayOfWeekOverride } : {}),
            ...(bookingSpeedOverride != null ? { bookingSpeedOverride } : {}),
            ...(hoursUntilArrival != null ? { hoursUntilArrival } : {}),
            ...(forceHoliday ? { forceHoliday: true } : {}),
            ...(forceBridgeDay ? { forceBridgeDay: true } : {}),
            ...(forceShoulderDay ? { forceShoulderDay: true } : {}),
            ...(forceGapFilling ? { forceGapFilling: true } : {}),
            ...(simulationMonth != null ? { simulationMonth } : {}),
            ...(demandOverride != null ? { demandOverride } : {}),
            ...(weatherOverride != null ? { weatherOverride } : {}),
        }),
    });
    if (!res.ok) throw new Error(`simulatePrice failed: ${res.status}`);
    return res.json();
}

// -- Pricing Defaults -------------------------------------------------------

export interface ReactivityValues {
    occupancyScaleMax: number;
    paceSensitivity: { lower: number; upper: number };
    occupancyDamping: number;
}

export interface LeadTimeCurveValues {
    /** Price uplift applied when the stay is beyond the typical booking window. */
    startPct: number;
    /** Max positive adjustment (uplift) at the demand peak, e.g. 0.10 = +10%. */
    peakPct: number;
    /** Max negative adjustment (discount) at arrival, e.g. -0.05 = -5%. */
    floorPct: number;
    /** Half-width (days) of the hold window around the median. */
    holdDays: number;
}

export interface PricingDefaults {
    /** Monday to Thursday ship at 0 — the base price is already the midweek rate. */
    mondayAdjustmentPct: number;
    tuesdayAdjustmentPct: number;
    wednesdayAdjustmentPct: number;
    thursdayAdjustmentPct: number;
    fridayAdjustmentPct: number;
    saturdayAdjustmentPct: number;
    sundayAdjustmentPct: number;
    optimizationGoal: "revenue" | "occupancy" | "hybrid";
    occupancyScaleMax: number;
    paceSensitivity: { lower: number; upper: number };
    occupancyDamping: number;
    propertyOccupancyWeight: number;
    propertyPaceWeight: number;
    propertyDemandWeight: number;
    propertyPickupWeight: number;
    defaultMinLos: number;
    losReleaseWithinDays: number;
    maxOccupancy: number;
    currency: string;
    priceGranularity: number;
    priceCharmEnding: number;
    forecastHorizonDays: number;
    pollIntervalMinutes: number;
    [key: string]: unknown;
}

export type ReactivityPresetKey = "gentle" | "moderate" | "aggressive";

export interface PricingDefaultsResponse {
    defaults: PricingDefaults;
    reactivityPresets: Record<string, Record<ReactivityPresetKey, ReactivityValues>>;
    leadTimeCurvePresets?: Record<string, Record<ReactivityPresetKey, LeadTimeCurveValues>>;
    /** Whether the engine reads a weekend's own bookings for its minimum stay. Absent from a server that predates it. */
    minLosDemandReading?: boolean;
}

// Module-level cache: fetched once per session, reused thereafter.
let _defaultsPromise: Promise<PricingDefaultsResponse> | null = null;

export function fetchPricingDefaults(): Promise<PricingDefaultsResponse> {
    if (!_defaultsPromise) {
        _defaultsPromise = apiFetch("/api/config/defaults")
            .then(res => {
                if (!res.ok) throw new Error(`fetchPricingDefaults failed: ${res.status}`);
                return res.json() as Promise<PricingDefaultsResponse>;
            })
            .catch(err => {
                _defaultsPromise = null; // Allow retry on failure
                throw err;
            });
    }
    return _defaultsPromise;
}

export function invalidatePricingDefaultsCache(): void {
    _defaultsPromise = null;
}
